import { useState } from "react";
import KuikaCheckbox from "../kuika-checkbox/kuika-checkbox";
import KuikaSmallText from "../typography/SmallText/SmallText";
import "./kuika-checkbox-group.scss";

export interface IKuikaCheckboxGroupOption {
  /** A node rather than a string so an option can carry an icon beside its name. */
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  description?: React.ReactNode;
  /**
   * The id on this option's input. Required when `label` is not a string, because the
   * historical `${label}Checkbox` id cannot be derived from a node.
   */
  dataTestId?: string;
}

export interface IKuikaCheckboxGroupProps {
  options: IKuikaCheckboxGroupOption[];
  value?: string[];
  defaultValue?: string[];
  onChange?: (selectedValues: string[]) => void;
  disabled?: boolean;
  direction?: "horizontal" | "vertical";
}

const KuikaCheckboxGroup = ({
  options,
  value: controlledValue,
  defaultValue = [],
  onChange,
  disabled = false,
  direction = "vertical"
}: IKuikaCheckboxGroupProps) => {
  const [internalValue, setInternalValue] = useState<string[]>(defaultValue);
  const isControlled: boolean = controlledValue !== undefined;
  const selectedValues: string[] = isControlled ? (controlledValue as string[]) : internalValue;

  const handleCheckboxChange = (optionValue: string, checked: boolean): void => {
    let newValues: string[];
    if (checked) {
      newValues = [...selectedValues, optionValue];
    } else {
      newValues = selectedValues.filter((val) => val !== optionValue);
    }

    if (!isControlled) {
      setInternalValue(newValues);
    }

    if (onChange) {
      onChange(newValues);
    }
  };

  const groupClassName: string = `kuika-checkbox-group kuika-checkbox-group--${direction}`;

  return (
    <div className={groupClassName}>
      {options.map((option) => (
        <div key={option.value} className="kuika-checkbox-group__item">
          <KuikaCheckbox
            label={option.label}
            checked={selectedValues.includes(option.value)}
            disabled={disabled || option.disabled}
            onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
            dataTestId={option.dataTestId}
          />
          {option.description && (
            <KuikaSmallText className="kuika-checkbox-group__description">{option.description}</KuikaSmallText>
          )}
        </div>
      ))}
    </div>
  );
};

export default KuikaCheckboxGroup;
